/* eslint-disable no-console */
import React, { PropsWithChildren, useMemo } from "react";

import { injectIntl } from "react-intl";
import { intlShape } from "../shapes";
import { getErrorTranslation, isSentry } from "../api";
import { logShape, withLog } from "./LogContext";

type backendShape = {
  get: $TSFixMeFunction;
  getAll: $TSFixMeFunction;
  post: $TSFixMeFunction;
  patch: $TSFixMeFunction;
  delete: $TSFixMeFunction;
  head: $TSFixMeFunction;
  upload: $TSFixMeFunction;
  translateError: $TSFixMeFunction;
};

type WithBackendProps = {
  backend: backendShape;
};

const BackendContext = React.createContext<WithBackendProps | null>(null);

type InternalBackendContextProviderProps = {
  backendApiClient: $TSFixMeFunction;
  intl: intlShape;
  testBackend?: $TSFixMe;
  log: logShape;
};

function InternalBackendContextProvider({
  backendApiClient,
  children,
  intl,
  log,
  testBackend,
}: PropsWithChildren<InternalBackendContextProviderProps>) {
  const context = useMemo(() => {
    if (testBackend) {
      const backend = testBackend;
      return { backend };
    }
    return {
      backend: {
        get: (
          url: $TSFixMe,
          parameters: $TSFixMe,
          cache = true,
          responseType = null,
        ) => backendApiClient(cache, true, responseType).get(url, parameters),
        getAll: (url: $TSFixMe, parameters: $TSFixMe, cache: $TSFixMe) =>
          backendApiClient(cache, true).getAll(url, parameters),
        post: (
          url: $TSFixMe,
          parametersOrObject: $TSFixMe,
          objectOrNull = null,
          redirectToSessionExpiredOnAuthError = true,
        ) =>
          backendApiClient(redirectToSessionExpiredOnAuthError, true).post(
            url,
            parametersOrObject,
            objectOrNull,
          ),
        patch: (
          url: $TSFixMe,
          parametersOrObject: $TSFixMe,
          objectOrNull = null,
        ) =>
          backendApiClient(true, true).patch(
            url,
            parametersOrObject,
            objectOrNull,
          ),
        delete: (
          url: $TSFixMe,
          parametersOrObject: $TSFixMe,
          objectOrNull = null,
        ) =>
          backendApiClient(true, true).delete(
            url,
            parametersOrObject,
            objectOrNull,
          ),
        head: (url: $TSFixMe, parameters: $TSFixMe) =>
          backendApiClient(true, true).head(url, parameters),
        upload: (url: $TSFixMe, parameters: $TSFixMe) =>
          backendApiClient(true, true).upload(url, parameters),
        translateError: (error: $TSFixMe) => {
          console.log(error);
          // all other errors should be handled!
          if (isSentry(error)) {
            log?.error("BackendContext", error);
          }
          return intl.formatMessage({ id: getErrorTranslation(error) });
        },
      },
    };
  }, []);
  return (
    <BackendContext.Provider value={context}>
      {children}
    </BackendContext.Provider>
  );
}

const BackendContextProvider = injectIntl(
  withLog(InternalBackendContextProvider),
);

const withBackend = <T extends WithBackendProps = WithBackendProps>(
  ChildComponent: React.ComponentType<T>,
) => {
  function ConnectedComponent(
    props: Readonly<Omit<T, keyof WithBackendProps>>,
  ) {
    return (
      <BackendContext.Consumer>
        {(context) => {
          // istanbul ignore next
          if (!context) {
            throw new Error(
              "withBackend must be used within a BackendContextProvider",
            );
          }
          return <ChildComponent {...(props as T)} {...context} />;
        }}
      </BackendContext.Consumer>
    );
  }
  ConnectedComponent.displayName =
    ChildComponent.displayName ?? ChildComponent.name;
  return ConnectedComponent;
};

export {
  BackendContextProvider,
  BackendContext,
  type backendShape,
  withBackend,
};
